import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useLocation } from 'react-router-dom'
import type { FnaFormData, ProgressionState, StepDefinition } from '../types'
import { useAuthContext } from './AuthContext'

const STORAGE_KEY = 'progression_state'
// Onboarding now lives entirely in the /profile step. The /conversation and
// /datacorrection routes are kept reachable by URL but are no longer part of
// the primary progression, so they don't appear here.
const ONBOARDING_STEP_IDS = new Set(['profile'])
const ONBOARDING_COMPLETE_THRESHOLD = 2

export const FLOW_STEPS: StepDefinition[] = [
  { id: 'accounts', label: 'Accounts', route: '/accounts', stepNumber: 1 },
  { id: 'profile', label: 'Profile', route: '/profile', stepNumber: 2 },
  { id: 'geminilive', label: 'Gemini Live', route: '/userquestionsgemini', stepNumber: 3 },
  { id: 'askclaude', label: 'Ask Claude', route: '/askclaude', stepNumber: 4 },
]

const MAX_STEP_NUMBER = FLOW_STEPS[FLOW_STEPS.length - 1]?.stepNumber ?? 1

interface ProgressionContextValue extends ProgressionState {
  visibleSteps: StepDefinition[]
  completeStep: (stepNumber: number) => void
  goToStep: (stepNumber: number) => void
  setFormData: (data: FnaFormData) => void
  mergeFormData: (partial: Partial<FnaFormData>) => void
  resetProgression: () => void
  getCurrentStepDef: () => StepDefinition
  getStepByRoute: (route: string) => StepDefinition | undefined
}

const defaultState: ProgressionState = {
  currentStep: 1,
  highestCompletedStep: 0,
  formData: null,
  stepCompletionTimestamps: {},
}

/**
 * Loads progression state from localStorage with fallback to defaults.
 *
 * @returns ProgressionState The persisted or default state
 */
function loadPersistedState(): ProgressionState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<ProgressionState>
      const merged = { ...defaultState, ...parsed }
      return {
        ...merged,
        currentStep: Math.min(Math.max(merged.currentStep ?? defaultState.currentStep, 1), MAX_STEP_NUMBER),
        highestCompletedStep: Math.min(
          Math.max(merged.highestCompletedStep ?? defaultState.highestCompletedStep, 0),
          MAX_STEP_NUMBER
        ),
      }
    }
  } catch (error) {
    console.error({
      message: 'Error loading progression state from localStorage',
      error: error instanceof Error ? error.message : String(error),
      context: { action: 'loadPersistedState' },
    })
  }
  return { ...defaultState }
}

/**
 * Persists progression state to localStorage.
 *
 * @param state - The state to persist
 */
function persistState(state: ProgressionState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch (error) {
    console.error({
      message: 'Error persisting progression state to localStorage',
      error: error instanceof Error ? error.message : String(error),
      context: { action: 'persistState' },
    })
  }
}

const ProgressionContext = createContext<ProgressionContextValue | undefined>(undefined)

interface ProgressionProviderProps {
  children: ReactNode
}

/**
 * Provider component managing the assessment flow state.
 *
 * @param props - Provider props containing children
 * @returns JSX.Element The provider wrapper
 */
export const ProgressionProvider = ({ children }: ProgressionProviderProps) => {
  const [state, setState] = useState<ProgressionState>(loadPersistedState)
  const location = useLocation()
  const { hasCompletedOnboarding } = useAuthContext()

  // Seed highestCompletedStep from backend when the user already completed
  // onboarding but localStorage doesn't reflect it (e.g. cleared browser data
  // or feature deployed after they finished).
  useEffect(() => {
    if (hasCompletedOnboarding && state.highestCompletedStep < ONBOARDING_COMPLETE_THRESHOLD) {
      setState(prev => ({
        ...prev,
        highestCompletedStep: Math.max(prev.highestCompletedStep, ONBOARDING_COMPLETE_THRESHOLD),
      }))
    }
  }, [hasCompletedOnboarding, state.highestCompletedStep])

  // Sync currentStep with the current route
  useEffect(() => {
    const step = FLOW_STEPS.find(s => s.route === location.pathname)
    if (step && step.stepNumber !== state.currentStep) {
      setState(prev => ({ ...prev, currentStep: step.stepNumber }))
    }
  }, [location.pathname, state.currentStep])

  // Persist state changes to localStorage
  useEffect(() => {
    persistState(state)
  }, [state])

  const completeStep = useCallback((stepNumber: number) => {
    setState(prev => ({
      ...prev,
      highestCompletedStep: Math.max(prev.highestCompletedStep, stepNumber),
      stepCompletionTimestamps: {
        ...prev.stepCompletionTimestamps,
        [stepNumber]: new Date().toISOString(),
      },
    }))
  }, [])

  const goToStep = useCallback((stepNumber: number) => {
    setState(prev => ({ ...prev, currentStep: Math.min(Math.max(stepNumber, 1), MAX_STEP_NUMBER) }))
  }, [])

  const setFormData = useCallback((data: FnaFormData) => {
    setState(prev => ({ ...prev, formData: data }))
    try {
      localStorage.setItem('form_data', JSON.stringify(data))
    } catch (error) {
      console.error({
        message: 'Error persisting form_data to localStorage',
        error: error instanceof Error ? error.message : String(error),
        context: { action: 'setFormData' },
      })
    }
  }, [])

  const mergeFormData = useCallback((partial: Partial<FnaFormData>) => {
    setState(prev => {
      const existing = prev.formData || {}
      const merged: FnaFormData = { ...existing }

      for (const [key, value] of Object.entries(partial)) {
        if (value === null || value === undefined) continue
        if (typeof value === 'string' && value.trim() === '') continue

        // Arrays (e.g. debts) replace entirely
        if (Array.isArray(value)) {
          merged[key] = value
        } else if (typeof value === 'string') {
          merged[key] = value.trim()
        } else {
          merged[key] = value
        }
      }

      try {
        localStorage.setItem('form_data', JSON.stringify(merged))
      } catch (error) {
        console.error({
          message: 'Error persisting merged form_data',
          error: error instanceof Error ? error.message : String(error),
          context: { action: 'mergeFormData' },
        })
      }

      return { ...prev, formData: merged }
    })
  }, [])

  const resetProgression = useCallback(() => {
    setState({ ...defaultState })
    try {
      localStorage.removeItem(STORAGE_KEY)
      localStorage.removeItem('form_data')
      localStorage.removeItem('results_summary')
    } catch (error) {
      console.error({
        message: 'Error clearing progression data',
        error: error instanceof Error ? error.message : String(error),
        context: { action: 'resetProgression' },
      })
    }
  }, [])

  const getCurrentStepDef = useCallback((): StepDefinition => {
    return FLOW_STEPS[state.currentStep - 1] || FLOW_STEPS[0]
  }, [state.currentStep])

  const getStepByRoute = useCallback((route: string): StepDefinition | undefined => {
    return FLOW_STEPS.find(s => s.route === route)
  }, [])

  const visibleSteps = useMemo(() => {
    if (state.highestCompletedStep >= ONBOARDING_COMPLETE_THRESHOLD) {
      return FLOW_STEPS.filter(s => !ONBOARDING_STEP_IDS.has(s.id))
    }
    return FLOW_STEPS
  }, [state.highestCompletedStep])

  const value = useMemo<ProgressionContextValue>(() => ({
    ...state,
    visibleSteps,
    completeStep,
    goToStep,
    setFormData,
    mergeFormData,
    resetProgression,
    getCurrentStepDef,
    getStepByRoute,
  }), [state, visibleSteps, completeStep, goToStep, setFormData, mergeFormData, resetProgression, getCurrentStepDef, getStepByRoute])

  return (
    <ProgressionContext.Provider value={value}>
      {children}
    </ProgressionContext.Provider>
  )
}

/**
 * Hook to access the progression context.
 *
 * @returns ProgressionContextValue The progression state and actions
 */
export const useProgressionContext = (): ProgressionContextValue => {
  const context = useContext(ProgressionContext)
  if (!context) {
    throw new Error('useProgressionContext must be used within a ProgressionProvider')
  }
  return context
}
